import _ from 'lodash';

import { getTextFromMetaData, getTwitterContentLength } from 'social/common/util/Util';
import {
    ViewState,
    PostType,
    ComponentType,
    PublisherError,
    SourceName,
    SourceCharLimit,
    SourceMediaLimit,
    GoogleButtonType,
    PublishPostType,
} from 'social/publisher/Publisher.types';
import { getNumOfSelectedSources } from 'social/publisher/util/Util';

export const MIN_CHARS_BY_SOURCE: SourceCharLimit = {
    FACEBOOK: 3,
    INSTAGRAM: 3,
    TWITTER: 1,
    GOOGLE_PLACES: 1,
    LINKEDIN: 1,
    YOUTUBE: 0,
    IG_STORIES: 0,
    IG_REELS: 0,
    TIKTOK: 0,
};
export const MAX_CHARS_BY_SOURCE: SourceCharLimit = {
    FACEBOOK: 60000,
    INSTAGRAM: 2200,
    TWITTER: 280,
    GOOGLE_PLACES: 1500,
    LINKEDIN: 1300,
    YOUTUBE: 5000,
    IG_STORIES: 2200,
    IG_REELS: 2200,
    TIKTOK: 300,
};
export const MAX_MEDIA_BY_SOURCE: SourceMediaLimit = {
    FACEBOOK: 40,
    INSTAGRAM: 10,
    TWITTER: 4,
    GOOGLE_PLACES: 1,
    LINKEDIN: 9,
    YOUTUBE: 1,
    IG_STORIES: 10,
    IG_REELS: 1,
    TIKTOK: 1,
};

export const MAX_CHARS_YOUTUBE_TITLE = 100;

const hasContent = (viewState: ViewState): boolean => {
    if (viewState.content.length > 0) {
        return true;
    }
    return false;
};

const hasTitle = (viewState: ViewState): boolean => {
    if (viewState.title && viewState.title.length > 0) {
        return true;
    }
    return false;
};

const hasMedia = (viewState: ViewState): boolean => {
    if (viewState.media.length > 0) {
        return true;
    }

    return false;
};

const hasImageMedia = (viewState: ViewState): boolean => {
    return (
        viewState.media.filter((media) => {
            return media && media.isImage === true;
        }).length > 0
    );
};

const hasVideoMedia = (viewState: ViewState): boolean => {
    return (
        viewState.media.filter((media) => {
            return media && media.isVideo === true;
        }).length > 0
    );
};

const hasAllMediaWithValidation = (viewState: ViewState): boolean => {
    let hasAllMediaWithValidation = true;

    if (hasMedia(viewState)) {
        // Note: media and mediaValidations will NOT always match up.
        // The source of truth for media is media[]. mediaValidation[] may cache validations for files that have been removed.

        viewState.media.forEach((mediaObject) => {
            const url = _.get(mediaObject, 'url');

            // Any missing validation means its going to return false
            if (hasAllMediaWithValidation && url) {
                const matchingMediaValidation = _.find(viewState.mediaValidation, (validation) => {
                    return validation.url === url;
                });

                if (!matchingMediaValidation) {
                    hasAllMediaWithValidation = false;
                }
            } else {
                hasAllMediaWithValidation = false;
            }
        });
    }

    return hasAllMediaWithValidation;
};

// TODO: verify this works properly
const hasAllMediaValidForSource = (viewState: ViewState, sourceName: SourceName): boolean => {
    let isAllValid = true;
    let publishPostType = PublishPostType.REGULAR;

    if (sourceName.includes('STORIES')) {
        publishPostType = PublishPostType.STORY;
    } else if (sourceName.includes('REELS')) {
        publishPostType = PublishPostType.REEL;
    }

    if (hasMedia(viewState)) {
        // Note: media and mediaValidations will NOT always match up.
        // The source of truth for media is media[]. mediaValidation[] may cache validations for files that have been removed.

        viewState.media.forEach((mediaObject, index) => {
            if (index < MAX_MEDIA_BY_SOURCE[sourceName]) {
                const url = _.get(mediaObject, 'url');

                // Any invalid media means we've determined at it's not all valid.
                if (isAllValid && url) {
                    const matchingMediaValidation = _.find(viewState.mediaValidation, (validation) => {
                        return validation.url === url && validation.publishPostType === publishPostType;
                    });

                    let sourceKey = sourceName;

                    // TODO: Add more validations for FB_STORIES and REELS when implemented
                    if (publishPostType === PublishPostType.STORY) {
                        if (sourceName === 'IG_STORIES') {
                            sourceKey = 'INSTAGRAM';
                        }
                    }

                    if (publishPostType === PublishPostType.REEL) {
                        if (sourceName === 'IG_REELS') {
                            sourceKey = 'INSTAGRAM';
                        }
                    }

                    const sourceValidation = _.get(matchingMediaValidation, `sourceValidation.${sourceKey}.valid`);

                    // We want to explicitly see that it is valid from the BE. All other cases fails.
                    if (sourceValidation !== true) {
                        isAllValid = false;
                    }
                } else {
                    isAllValid = false;
                }
            }
        });
    }

    return isAllValid;
};

const hasSingleMediaType = (viewState: ViewState): boolean => {
    return !(hasImageMedia(viewState) && hasVideoMedia(viewState));
};

const hasLink = (viewState: ViewState): boolean => {
    let hasLink = false;

    viewState.content.forEach((val) => {
        const linkAttr = _.get(val, `attributes.link`);

        if (val && linkAttr) {
            hasLink = true;
        }
    });

    return hasLink;
};

const hasASourceSelected = (viewState: ViewState): boolean => {
    return getNumOfSelectedSources(viewState.selectedSources) > 0;
};

const hasALocationSelected = (viewState: ViewState): boolean => {
    return viewState.selectedLocations.length > 0;
};

const hasALabelSelected = (viewState: ViewState): boolean => {
    return (viewState.selectedLabelIds && viewState.selectedLabelIds.length > 0) || viewState.selectedLabels.length > 0;
};

const hasPromisesInTransit = (viewState: ViewState): boolean => {
    if (viewState.promisesInTransit.length > 0) {
        return true;
    }

    return false;
};

const getSelectedLocationLength = (viewState: ViewState): number => {
    return viewState.selectedLocations.length;
};

const getPostType = (viewState: ViewState): PostType => {
    return viewState.postType || 'MEDIA';
};

const getContentLength = (viewState: ViewState): number => {
    return getTextFromMetaData(viewState.content).length;
};

const getMediaLength = (viewState: ViewState): number => {
    return viewState.media.length;
};

const getTitleLength = (viewState: ViewState): number => {
    let titleLength = 0;

    if (viewState.title) {
        titleLength = viewState.title.length;
    }
    return titleLength;
};

const hasAngleBracket = (text: string): boolean => {
    if (text.indexOf('<') > -1 || text.indexOf('>') > -1) {
        return true;
    }
    return false;
};

const hasAngleBracketInTitle = (viewState: ViewState): boolean => {
    if (viewState.title && hasAngleBracket(viewState.title)) {
        return true;
    }
    return false;
};

const hasAngleBracketInContent = (viewState: ViewState): boolean => {
    if (hasAngleBracket(getTextFromMetaData(viewState.content))) {
        return true;
    }
    return false;
};

const hasUrlMetaData = (viewState: ViewState): boolean => {
    if (viewState.urlMetaData && viewState.urlMetaData.url) {
        return true;
    }
    return false;
};

// TODO: Future expansion of field?: string' as additional argument to target specific fields in a component
const createError = (source: SourceName | null, component: ComponentType, message: string): PublisherError => {
    return {
        source,
        component,
        message,
    };
};

// Validates Posts
export const ValidatePost = (viewState: ViewState): PublisherError[] => {
    const errors: PublisherError[] = [];
    const postType = getPostType(viewState);
    const selectedLocationLength = getSelectedLocationLength(viewState);
    const contentLength = getContentLength(viewState);
    const mediaLength = getMediaLength(viewState);
    const titleLength = getTitleLength(viewState);
    const isLibraryContentCreateMode = viewState.editorMode === 'CONTENT_CREATE';

    // General Validations
    if (!hasASourceSelected(viewState) && viewState.publishType !== 'DRAFT') {
        errors.push(createError(null, 'SOURCE', 'SOURCE_IS_REQUIRED'));
    }
    // todo: consider isLibraryContentCreateMode
    if (
        !hasALocationSelected(viewState) &&
        viewState.editorMode !== 'CAMPAIGN_CREATE' &&
        viewState.editorMode !== 'CONTENT_CREATE' &&
        viewState.publishType !== 'DRAFT'
    ) {
        errors.push(createError(null, 'LOCATION', 'LOCATION_IS_REQUIRED'));
    }
    if (hasPromisesInTransit(viewState)) {
        errors.push(createError(null, 'PUBLISHER', 'DATA_TRANSFER_NOT_COMPLETE'));
    }
    if (isLibraryContentCreateMode) {
        if (!hasALabelSelected(viewState)) {
            errors.push(createError(null, 'LABELS', 'LABEL_IS_REQUIRED'));
        }
    }

    // Source-specific Validations
    if (viewState.selectedSources.FACEBOOK) {
        if (!hasSingleMediaType(viewState)) {
            errors.push(createError('FACEBOOK', 'MEDIA', 'MIXED_MEDIA_NOT_ALLOWED'));
        }

        if (hasContent(viewState) && contentLength < MIN_CHARS_BY_SOURCE.FACEBOOK) {
            errors.push(createError('FACEBOOK', 'CONTENT', 'CONTENT_IS_TOO_SHORT'));
        }

        if (contentLength > MAX_CHARS_BY_SOURCE.FACEBOOK) {
            errors.push(createError('FACEBOOK', 'CONTENT', 'CONTENT_IS_TOO_LONG'));
        }

        if (!hasContent(viewState) && !hasMedia(viewState)) {
            errors.push(createError('FACEBOOK', 'CONTENT', 'CONTENT_OR_MEDIA_IS_REQUIRED'));
        }

        if (hasMedia(viewState) && postType === 'MEDIA') {
            if (!hasAllMediaWithValidation(viewState)) {
                errors.push(createError('FACEBOOK', 'MEDIA', 'MEDIA_VALIDATIONS_NOT_COMPLETE'));
            } else if (!hasAllMediaValidForSource(viewState, 'FACEBOOK')) {
                errors.push(createError('FACEBOOK', 'MEDIA', 'MEDIA_FILES_ARE_INVALID'));
            }
        }

        if (postType === 'LINK' && !hasUrlMetaData(viewState)) {
            errors.push(createError('FACEBOOK', 'CONTENT', 'LINK_IS_INVALID'));
        }
    }

    if (viewState.selectedSources.TWITTER) {
        if (!hasSingleMediaType(viewState)) {
            errors.push(createError('TWITTER', 'MEDIA', 'MIXED_MEDIA_NOT_ALLOWED'));
        }

        if (selectedLocationLength > 1 && !isLibraryContentCreateMode) {
            errors.push(createError('TWITTER', 'LOCATION', 'TWITTER_ONLY_ALLOWS_ONE_LOCATION'));
        }

        if (hasContent(viewState) && getTwitterContentLength(viewState.content) > MAX_CHARS_BY_SOURCE.TWITTER) {
            errors.push(createError('TWITTER', 'CONTENT', 'CONTENT_IS_TOO_LONG'));
        }

        if (!hasContent(viewState) && !hasMedia(viewState)) {
            errors.push(createError('TWITTER', 'CONTENT', 'CONTENT_OR_MEDIA_IS_REQUIRED'));
        }

        if (hasMedia(viewState) && postType === 'MEDIA') {
            if (!hasAllMediaWithValidation(viewState)) {
                errors.push(createError('TWITTER', 'MEDIA', 'MEDIA_VALIDATIONS_NOT_COMPLETE'));
            } else if (!hasAllMediaValidForSource(viewState, 'TWITTER')) {
                errors.push(createError('TWITTER', 'MEDIA', 'MEDIA_FILES_ARE_INVALID'));
            }
        }

        if (postType === 'LINK' && !hasUrlMetaData(viewState)) {
            errors.push(createError('TWITTER', 'CONTENT', 'LINK_IS_INVALID'));
        }
    }

    if (viewState.selectedSources.INSTAGRAM) {
        if (contentLength < MIN_CHARS_BY_SOURCE.INSTAGRAM) {
            errors.push(createError('INSTAGRAM', 'CONTENT', 'CONTENT_IS_TOO_SHORT'));
        }

        if (!hasMedia(viewState)) {
            errors.push(createError('INSTAGRAM', 'MEDIA', 'INSTAGRAM_REQUIRES_MEDIA'));
        }

        if (contentLength > MAX_CHARS_BY_SOURCE.INSTAGRAM) {
            errors.push(createError('INSTAGRAM', 'CONTENT', 'CONTENT_IS_TOO_LONG'));
        }

        if (hasMedia(viewState) && postType === 'MEDIA') {
            if (!hasAllMediaWithValidation(viewState)) {
                errors.push(createError('INSTAGRAM', 'MEDIA', 'MEDIA_VALIDATIONS_NOT_COMPLETE'));
            } else if (!hasAllMediaValidForSource(viewState, 'INSTAGRAM')) {
                errors.push(createError('INSTAGRAM', 'MEDIA', 'MEDIA_FILES_ARE_INVALID'));
            }
        }

        if (postType === 'LINK' && hasLink(viewState)) {
            errors.push(createError('INSTAGRAM', 'LINK', 'LINK_POSTS_NOT_ALLOWED'));
        }
    }

    if (viewState.selectedSources.IG_REELS) {
        if (contentLength < MIN_CHARS_BY_SOURCE.IG_REELS) {
            errors.push(createError('IG_REELS', 'CONTENT', 'CONTENT_IS_TOO_SHORT'));
        }

        if (contentLength > MAX_CHARS_BY_SOURCE.IG_REELS) {
            errors.push(createError('IG_REELS', 'CONTENT', 'CONTENT_IS_TOO_LONG'));
        }

        if (!hasMedia(viewState)) {
            errors.push(createError('IG_REELS', 'MEDIA', 'VIDEO_IS_REQUIRED'));
        }

        if (hasImageMedia(viewState) && postType === 'MEDIA') {
            errors.push(createError('IG_REELS', 'MEDIA', 'IMAGES_NOT_ALLOWED'));
        }

        if (hasMedia(viewState) && postType === 'MEDIA') {
            if (!hasAllMediaWithValidation(viewState)) {
                errors.push(createError('IG_REELS', 'MEDIA', 'MEDIA_VALIDATIONS_NOT_COMPLETE'));
            } else if (!hasAllMediaValidForSource(viewState, 'IG_REELS')) {
                errors.push(createError('IG_REELS', 'MEDIA', 'MEDIA_FILES_ARE_INVALID'));
            }
        }

        if (postType === 'LINK' && hasLink(viewState)) {
            errors.push(createError('IG_REELS', 'LINK', 'LINK_POSTS_NOT_ALLOWED'));
        }
    }

    if (viewState.selectedSources.GOOGLE_PLACES) {
        if (!hasContent(viewState)) {
            errors.push(createError('GOOGLE_PLACES', 'CONTENT', 'TEXT_CONTENT_IS_REQUIRED'));
        } else {
            if (contentLength < MIN_CHARS_BY_SOURCE.GOOGLE_PLACES) {
                errors.push(createError('GOOGLE_PLACES', 'CONTENT', 'CONTENT_IS_TOO_SHORT'));
            }

            if (contentLength > MAX_CHARS_BY_SOURCE.GOOGLE_PLACES) {
                errors.push(createError('GOOGLE_PLACES', 'CONTENT', 'Content is too long'));
            }
        }

        if (mediaLength > 1 && postType === 'MEDIA') {
            errors.push(createError('GOOGLE_PLACES', 'MEDIA', 'ONLY_ONE_MEDIA_IS_ALLOWED'));
        }

        if (hasVideoMedia(viewState) && postType === 'MEDIA') {
            errors.push(createError('GOOGLE_PLACES', 'MEDIA', 'VIDEOS_NOT_ALLOWED'));
        }

        if (hasMedia(viewState) && postType === 'MEDIA') {
            if (!hasAllMediaWithValidation(viewState)) {
                errors.push(createError('GOOGLE_PLACES', 'MEDIA', 'MEDIA_VALIDATIONS_NOT_COMPLETE'));
            } else if (!hasAllMediaValidForSource(viewState, 'GOOGLE_PLACES')) {
                errors.push(createError('GOOGLE_PLACES', 'MEDIA', 'MEDIA_FILES_ARE_INVALID'));
            }
        }

        if (postType === 'LINK' && hasLink(viewState)) {
            errors.push(createError('GOOGLE_PLACES', 'LINK', 'LINK_POSTS_NOT_ALLOWED'));
        }

        if (viewState.googleFields.event) {
            const eventTitle = _.get(viewState.googleFields, 'event.title');

            if (!eventTitle) {
                errors.push(createError('GOOGLE_PLACES', 'GOOGLE_FIELDS', 'GOOGLE_EVENT_REQUIRES_TITLE'));
            }
        }

        if (viewState.googleFields.callToAction) {
            const buttonType = _.get(viewState.googleFields, 'callToAction.buttonType');
            const linkValue = _.get(viewState.googleFields, 'callToAction.link');

            if (buttonType !== GoogleButtonType.CALL && !linkValue) {
                errors.push(createError('GOOGLE_PLACES', 'GOOGLE_FIELDS', 'GOOGLE_BUTTON_REQUIRES_URL'));
            }
        }

        if (viewState.googleFields.alertType && hasMedia(viewState)) {
            errors.push(createError('GOOGLE_PLACES', 'GOOGLE_FIELDS', 'GOOGLE_COVID_ALERT_MEDIA_NOT_ALLOWED'));
        }
    }

    if (viewState.selectedSources.LINKEDIN) {
        if (!hasSingleMediaType(viewState)) {
            errors.push(createError('LINKEDIN', 'MEDIA', 'MIXED_MEDIA_NOT_ALLOWED'));
        }

        if (selectedLocationLength > 1 && !isLibraryContentCreateMode) {
            errors.push(createError('LINKEDIN', 'LOCATION', 'LINKEDIN_ONLY_ALLOWS_ONE_LOCATION'));
        }

        if (hasContent(viewState) && contentLength < MIN_CHARS_BY_SOURCE.LINKEDIN) {
            errors.push(createError('LINKEDIN', 'CONTENT', 'CONTENT_IS_TOO_SHORT'));
        }

        if (contentLength > MAX_CHARS_BY_SOURCE.LINKEDIN) {
            errors.push(createError('LINKEDIN', 'CONTENT', 'CONTENT_IS_TOO_LONG'));
        }

        if (!hasContent(viewState) && !hasMedia(viewState)) {
            errors.push(createError('LINKEDIN', 'CONTENT', 'CONTENT_OR_MEDIA_IS_REQUIRED'));
        }

        if (hasMedia(viewState) && postType === 'MEDIA') {
            if (!hasAllMediaWithValidation(viewState)) {
                errors.push(createError('LINKEDIN', 'MEDIA', 'MEDIA_VALIDATIONS_NOT_COMPLETE'));
            } else if (!hasAllMediaValidForSource(viewState, 'LINKEDIN')) {
                errors.push(createError('LINKEDIN', 'MEDIA', 'MEDIA_FILES_ARE_INVALID'));
            }
        }

        if (postType === 'LINK' && !hasUrlMetaData(viewState)) {
            errors.push(createError('LINKEDIN', 'CONTENT', 'LINK_IS_INVALID'));
        }
    }

    if (viewState.selectedSources.YOUTUBE) {
        if (!hasTitle(viewState)) {
            errors.push(createError('YOUTUBE', 'TITLE', 'TITLE_IS_REQUIRED'));
        } else {
            if (titleLength > MAX_CHARS_YOUTUBE_TITLE) {
                errors.push(createError('YOUTUBE', 'TITLE', 'TITLE_IS_TOO_LONG'));
            }
            if (hasAngleBracketInTitle(viewState)) {
                errors.push(createError('YOUTUBE', 'TITLE', 'TITLE_ANGLE_BRACKETS_NOT_ALLOWED'));
            }
        }

        if (hasContent(viewState)) {
            if (contentLength > MAX_CHARS_BY_SOURCE.YOUTUBE) {
                errors.push(createError('YOUTUBE', 'CONTENT', 'DESCRIPTION_IS_TOO_LONG'));
            }
            if (hasAngleBracketInContent(viewState)) {
                errors.push(createError('YOUTUBE', 'CONTENT', 'DESCRIPTION_ANGLE_BRACKETS_NOT_ALLOWED'));
            }
        }

        if (mediaLength > 1 && postType === 'MEDIA') {
            errors.push(createError('YOUTUBE', 'MEDIA', 'ONLY_ONE_MEDIA_IS_ALLOWED'));
        }

        if (!hasMedia(viewState)) {
            errors.push(createError('YOUTUBE', 'MEDIA', 'VIDEO_IS_REQUIRED'));
        }

        if (hasImageMedia(viewState) && postType === 'MEDIA') {
            errors.push(createError('YOUTUBE', 'MEDIA', 'IMAGES_NOT_ALLOWED'));
        }

        if (hasMedia(viewState) && postType === 'MEDIA') {
            if (!hasAllMediaWithValidation(viewState)) {
                errors.push(createError('YOUTUBE', 'MEDIA', 'MEDIA_VALIDATIONS_NOT_COMPLETE'));
            } else if (!hasAllMediaValidForSource(viewState, 'YOUTUBE')) {
                errors.push(createError('YOUTUBE', 'MEDIA', 'MEDIA_FILES_ARE_INVALID'));
            }
        }

        if (postType === 'LINK' && hasLink(viewState)) {
            errors.push(createError('YOUTUBE', 'LINK', 'LINK_POSTS_NOT_ALLOWED'));
        }
    }

    if (viewState.selectedSources.IG_STORIES) {
        if (!hasSingleMediaType(viewState)) {
            errors.push(createError('IG_STORIES', 'MEDIA', 'MIXED_MEDIA_NOT_ALLOWED'));
        }

        if (contentLength < MIN_CHARS_BY_SOURCE.IG_STORIES) {
            errors.push(createError('IG_STORIES', 'CONTENT', 'CONTENT_IS_TOO_SHORT'));
        }

        if (!hasMedia(viewState)) {
            errors.push(createError('IG_STORIES', 'MEDIA', 'IG_STORIES_REQUIRES_MEDIA'));
        }

        if (contentLength > MAX_CHARS_BY_SOURCE.IG_STORIES) {
            errors.push(createError('IG_STORIES', 'CONTENT', 'CONTENT_IS_TOO_LONG'));
        }

        if (hasMedia(viewState) && postType === 'MEDIA') {
            if (!hasAllMediaWithValidation(viewState)) {
                errors.push(createError('IG_STORIES', 'MEDIA', 'MEDIA_VALIDATIONS_NOT_COMPLETE'));
            } else if (!hasAllMediaValidForSource(viewState, 'IG_STORIES')) {
                // TODO: Potentially change to !hasAllMediaValidForSource(viewState, 'IG_STORIES')
                errors.push(createError('IG_STORIES', 'MEDIA', 'MEDIA_FILES_ARE_INVALID'));
            }
        }

        if (postType === 'LINK' && hasLink(viewState)) {
            errors.push(createError('IG_STORIES', 'LINK', 'LINK_POSTS_NOT_ALLOWED'));
        }
    }

    if (viewState.selectedSources.TIKTOK) {
        if (hasContent(viewState) && contentLength > MAX_CHARS_BY_SOURCE.TIKTOK) {
            errors.push(createError('TIKTOK', 'CONTENT', 'DESCRIPTION_IS_TOO_LONG'));
        }

        if (mediaLength > 1 && postType === 'MEDIA') {
            errors.push(createError('TIKTOK', 'MEDIA', 'ONLY_ONE_MEDIA_IS_ALLOWED'));
        }

        if (!hasMedia(viewState)) {
            errors.push(createError('TIKTOK', 'MEDIA', 'VIDEO_IS_REQUIRED'));
        }

        if (hasImageMedia(viewState) && postType === 'MEDIA') {
            errors.push(createError('TIKTOK', 'MEDIA', 'IMAGES_NOT_ALLOWED'));
        }

        if (hasMedia(viewState) && postType === 'MEDIA') {
            if (!hasAllMediaWithValidation(viewState)) {
                errors.push(createError('TIKTOK', 'MEDIA', 'MEDIA_VALIDATIONS_NOT_COMPLETE'));
            } else if (!hasAllMediaValidForSource(viewState, 'TIKTOK')) {
                errors.push(createError('TIKTOK', 'MEDIA', 'MEDIA_FILES_ARE_INVALID'));
            }
        }

        if (postType === 'LINK' && hasLink(viewState)) {
            errors.push(createError('TIKTOK', 'LINK', 'LINK_POSTS_NOT_ALLOWED'));
        }
    }

    return errors;
};
